import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import AppearanceSettings from './AppearanceSettings';
import ResponseToneSettings from './ResponseToneSettings';
import { Button } from './ui/button';
import { Card } from './ui/card';
import InfoTip from './InfoTip';

const sameStatus = (left, right) => (
  left.ready === right.ready && left.dirty === right.dirty && left.saving === right.saving
);

// Admin passes showTheme={false}: the personal theme lives in each person's profile.
const GeneralSettingsPanel = ({ showResponseTone = false, showTheme = true }) => {
  const toneRef = useRef(null);
  const themeRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState({
    tone: { ready: !showResponseTone, dirty: false, saving: false },
    theme: { ready: !showTheme, dirty: false, saving: false },
  });

  const updateStatus = useCallback((key, status) => {
    setStatuses((current) => {
      const next = { ready: Boolean(status.ready), dirty: Boolean(status.dirty), saving: Boolean(status.saving) };
      return sameStatus(current[key], next) ? current : { ...current, [key]: next };
    });
  }, []);
  const toneStatus = useCallback((status) => updateStatus('tone', status), [updateStatus]);
  const themeStatus = useCallback((status) => updateStatus('theme', status), [updateStatus]);

  const activeStatuses = useMemo(() => [
    ...(showResponseTone ? [statuses.tone] : []),
    ...(showTheme ? [statuses.theme] : []),
  ], [showResponseTone, showTheme, statuses]);
  const dirty = activeStatuses.some((status) => status.dirty);
  // A temporary outage in one independent settings service must not prevent
  // saving valid changes made in the other section.
  const ready = activeStatuses.filter((status) => status.dirty).every((status) => status.ready);

  const saveAll = async () => {
    const sections = [
      ...(showResponseTone ? [{ name: 'Response tone', ref: toneRef }] : []),
      ...(showTheme ? [{ name: 'Themes', ref: themeRef }] : []),
    ].filter((section) => section.ref.current?.isDirty?.());
    if (!sections.length) return;

    setSaving(true);
    const results = await Promise.allSettled(sections.map((section) => section.ref.current.save()));
    setSaving(false);
    const failed = results
      .map((result, index) => result.status === 'rejected' ? sections[index].name : null)
      .filter(Boolean);
    if (!failed.length) {
      toast.success('General settings saved');
    } else if (failed.length === sections.length) {
      toast.error('General settings could not be saved. Your changes remain available to retry.');
    } else {
      toast.error(`Some settings were saved. Retry: ${failed.join(', ')}.`);
    }
  };

  return (
    <div className="space-y-6" data-testid="general-settings-panel">
      {showResponseTone && <ResponseToneSettings ref={toneRef} showSaveAction={false} onStatusChange={toneStatus} />}
      {showTheme && <Card className="p-6">
        <AppearanceSettings ref={themeRef} showSaveAction={false} onStatusChange={themeStatus} />
      </Card>}

      <Card className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 p-4 shadow-lg" style={{ background: 'var(--qflow-surface)', borderColor: 'var(--qflow-border)', color: 'var(--qflow-text)' }}>
        <div className="flex items-center gap-2 text-sm font-semibold">
          {dirty ? 'You have unsaved changes' : 'All changes saved'}
          <InfoTip label="About saving General Settings">Save all General Settings changes together. Tenant preferences and your personal theme keep their existing scopes.</InfoTip>
        </div>
        <Button type="button" onClick={saveAll} disabled={!ready || !dirty || saving} data-testid="save-general-settings">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving changes…' : 'Save changes'}
        </Button>
      </Card>
    </div>
  );
};

export default GeneralSettingsPanel;
